import type { TFunction } from 'i18next'
import { EDITOR_NAME_PART_KEYS } from '@messenger/lotus'
import type { EditorContentRoleValue } from '@messenger/lotus/row-store'
import { additionalFamilyTypes, formatFamilyType } from '@messenger/utils'
import { fieldLabel } from './fieldCatalog'
import type { FieldMenuLayout, FieldMenuRow } from './fieldMenuGroups'
import { PARAGRAPH_ROLE_LABELS } from './paragraphRoles'

/**
 * The `editor.textBox` key segment for a catalog key, a colour name, a font
 * region or a field group: lowercased, every run of anything but a letter or
 * a digit one `_`. i18next reads `:` as its namespace separator, so `fh:name`
 * cannot reach it as is; `Mother's side`, `Light Gray` and
 * `European & Historic` follow the same rule. The English name stays the id
 * in every data attribute and comparison; only the rendered text changes.
 */
export const editorTextBoxI18nSlug = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')

/** A string under `editor.textBox`, the English source string as the fallback. */
const textBoxText = (t: TFunction, key: string, english: string): string =>
  String(t(`editor.textBox.${key}`, { defaultValue: english }))

export const editorTextBoxFieldLabel = (t: TFunction, key: string): string =>
  textBoxText(t, `fields.${editorTextBoxI18nSlug(key)}`, fieldLabel(key))

/**
 * The label of a table's menu row and of its rows modal. An additional-family
 * table naming a group other than the bucket is labelled the way the case
 * form's Additional Family section titles that group; every other table, the
 * bucket's included, takes its field's label.
 */
export const editorTextBoxTableLabel = (
  t: TFunction,
  fieldKey: string,
  rowType?: string,
): string =>
  fieldKey === 'additional_family' &&
  rowType !== undefined &&
  rowType !== additionalFamilyTypes.additionalFamily
    ? String(
        t(`caseFormSectionsLabels.${rowType}`, {
          defaultValue: formatFamilyType(rowType),
        }),
      )
    : editorTextBoxFieldLabel(t, fieldKey)

export const editorTextBoxNamePartLabel = (
  t: TFunction,
  part: string,
): string => {
  const key = EDITOR_NAME_PART_KEYS[part]
  return key === undefined ? part : editorTextBoxFieldLabel(t, key)
}

export const editorTextBoxFieldGroupLabel = (
  t: TFunction,
  group: string,
): string =>
  textBoxText(t, `fieldGroups.${editorTextBoxI18nSlug(group)}`, group)

/**
 * An Insert detail menu row's label: a case's service or visitation by its
 * venue, else by its kind and its number among that kind's additional rows;
 * an additional-family group other than the bucket as the case form titles
 * it; every other row by the menu's own label for it, else its field's.
 */
export const editorTextBoxFieldMenuRowLabel = (
  t: TFunction,
  row: FieldMenuRow,
): string => {
  const { entry, label, rowType, service } = row
  if (service) {
    const noun = String(
      entry.key === 'visitation'
        ? t('editor.textBox.toolbar.visitationNoun')
        : t('editor.textBox.toolbar.serviceNoun'),
    )
    return String(
      service.location
        ? t('editor.textBox.toolbar.serviceAtLocation', {
            noun,
            location: service.location,
          })
        : t('editor.textBox.toolbar.serviceNumbered', {
            noun,
            number: service.number,
          }),
    )
  }
  if (
    rowType !== undefined &&
    rowType !== additionalFamilyTypes.additionalFamily
  )
    return editorTextBoxTableLabel(t, entry.key, rowType)
  return label === undefined
    ? editorTextBoxFieldLabel(t, entry.key)
    : textBoxText(t, `fieldMenu.${editorTextBoxI18nSlug(entry.key)}`, label)
}

/** An Insert detail layout row's label: the menu's own, for what the layout holds. */
export const editorTextBoxFieldMenuLayoutLabel = (
  t: TFunction,
  layout: FieldMenuLayout,
): string =>
  textBoxText(t, `fieldMenu.${editorTextBoxI18nSlug(layout.id)}`, layout.label)

export const editorTextBoxRoleLabel = (
  t: TFunction,
  role: EditorContentRoleValue,
): string => textBoxText(t, `roles.${role}`, PARAGRAPH_ROLE_LABELS[role])

export const editorTextBoxColorName = (t: TFunction, name: string): string =>
  textBoxText(t, `colors.${editorTextBoxI18nSlug(name)}`, name)

export const editorTextBoxFontRegionLabel = (
  t: TFunction,
  region: string,
): string =>
  textBoxText(t, `fontRegions.${editorTextBoxI18nSlug(region)}`, region)
